import { useCallback, useEffect, useMemo } from 'react';

import { useParams } from 'react-router';

import { useUpdateDashboard } from '@/features/Dashboard/api';
import { HIGHLIGHTED_PRICE_MAX_ITEMS } from '@/features/Dashboard/constants/const';
import { TAB_DEFAULT_BASE_NAME } from '@/features/Dashboard/constants/names';
import { useDrawerSteps } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { useBuildDashboardStructure } from '@/features/Dashboard/hooks/useBuildDashboardStructure';
import { useCurrentDashboardConfig } from '@/features/Dashboard/hooks/useCurrentDashboardConfig';
import { getTabContent } from '@/features/Dashboard/lib/transformers';
import { useDashboardEditActions } from '@/features/Dashboard/pages/Edit/hooks/useDashboardEditActions';
import {
  selectCurrentTabId,
  selectInitialDashboardName
} from '@/features/Dashboard/pages/Edit/model/dashboardEdit.selectors';
import type { MarketApp } from '@/features/Dashboard/types';
import { useAppSelector } from '@/hooks/redux-hooks';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';

import { handleEditModeSave, wouldExceedTilesLimit } from './utils';

export const useSaveApp = () => {
  const { dashboardId, tabName } = useParams<{ dashboardId?: string; tabName?: string }>();
  const initialDashboardNameFromRedux = useAppSelector(selectInitialDashboardName);
  const initialDashboardName =
    initialDashboardNameFromRedux || (dashboardId ? decodeURIComponent(dashboardId) : '');
  const currentTabId = useAppSelector(selectCurrentTabId);
  const { closeDrawer, isEditMode, editingTarget, setEditingTarget } = useDrawerSteps();
  const actions = useDashboardEditActions();
  const allDashboardTabs = useCurrentDashboardConfig();
  const { showErrorSnackbar } = useSnackbarNotifications();

  const currentTabName = tabName || Object.keys(allDashboardTabs || {})[0] || TAB_DEFAULT_BASE_NAME;

  const buildDashboardStructure = useBuildDashboardStructure();

  const { mutate, isSuccess, isPending } = useUpdateDashboard(
    initialDashboardName,
    'New app added!',
    currentTabName
  );

  const currentTilesCount = useMemo(() => {
    if (!allDashboardTabs) return 0;
    const tabValue = allDashboardTabs[currentTabName];
    if (!tabValue) return 0;
    const tabContent = getTabContent(tabValue);
    const tilesWidget = tabContent.find(app => 'type' in app);
    return tilesWidget && 'tiles' in tilesWidget ? tilesWidget.tiles.length : 0;
  }, [allDashboardTabs, currentTabName]);

  useEffect(() => {
    if (isSuccess) {
      closeDrawer();
    }
  }, [isSuccess, closeDrawer]);

  const handleSave = useCallback(
    (widget: MarketApp | undefined) => {
      if (!widget) return;
      if (isEditMode && !currentTabId) return;

      if (isEditMode) {
        handleEditModeSave({
          actions,
          widget,
          currentTabId,
          editingTarget,
          setEditingTarget,
          closeDrawer
        });
        return;
      }

      if (wouldExceedTilesLimit(widget, currentTilesCount)) {
        showErrorSnackbar(
          `Maximum ${HIGHLIGHTED_PRICE_MAX_ITEMS} highlighted prices allowed per tab.`
        );
        return;
      }

      const structurePayload = buildDashboardStructure(widget);
      mutate(structurePayload);
    },
    [
      actions,
      buildDashboardStructure,
      closeDrawer,
      currentTabId,
      currentTilesCount,
      editingTarget,
      isEditMode,
      mutate,
      setEditingTarget,
      showErrorSnackbar
    ]
  );

  return { handleSave, isPending };
};
